import React, { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Phone, Mail, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { AGENCY } from "@/config/agency";

export default function Chatbot() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(`chat_${Math.random().toString(36).slice(2)}`);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && msgs.length === 0) {
      setMsgs([{ role: "bot", text: lang === "fr"
        ? "Bienvenue. Je suis le concierge HorsSignal. Quelle expédition vous intrigue ?"
        : "Welcome. I'm the HorsSignal concierge. Which expedition intrigues you?" }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId.current, message: text });
      setMsgs((m) => [...m, { role: "bot", text: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "bot", text: "..." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed bottom-6 right-24 z-[60] hs-float">
        {contactOpen && (
          <div data-testid="contact-menu" className="absolute bottom-16 right-0 w-64 bg-[#0F0F0F] border border-white/15 shadow-xl fade-up">
            <div className="px-4 py-3 border-b border-white/10">
              <span className="font-clash uppercase tracking-tight text-sm">{t("contact_us")}</span>
            </div>
            <a
              data-testid="contact-whatsapp"
              href={`https://wa.me/${AGENCY.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5"
            >
              <MessageCircle size={18} className="text-[#BC5D2E] shrink-0" />
              <div className="flex flex-col">
                <span className="text-sm text-[#F5F5F0]">WhatsApp / Tél</span>
                <span className="font-mono-hs text-[10px] text-[#737373] uppercase tracking-widest">9H – 18H</span>
              </div>
            </a>
            <a
              data-testid="contact-email"
              href="mailto:contact@horssignal.com"
              className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors"
            >
              <Mail size={18} className="text-[#BC5D2E] shrink-0" />
              <div className="flex flex-col">
                <span className="text-sm text-[#F5F5F0]">Email</span>
                <span className="font-mono-hs text-[10px] text-[#737373]">contact@horssignal.com</span>
              </div>
            </a>
          </div>
        )}
        <button
          data-testid="contact-btn"
          onClick={() => setContactOpen(!contactOpen)}
          title={t("contact_us")}
          className="w-14 h-14 bg-[#141414] border border-white/15 hover:border-[#BC5D2E]/60 flex items-center justify-center transition-colors shadow-lg"
        >
          {contactOpen ? <X className="text-white" size={20} /> : <Phone className="text-[#BC5D2E]" size={20} />}
        </button>
      </div>
      <button
        data-testid="chatbot-toggle"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-[60] hs-float w-14 h-14 bg-[#BC5D2E] hover:bg-[#8A281E] flex items-center justify-center transition-colors shadow-lg"
      >
        {open ? <X className="text-white" /> : <MessageSquare className="text-white" />}
      </button>
      {open && (
        <div data-testid="chatbot-panel" className="fixed bottom-24 right-6 z-[60] w-[90vw] max-w-sm h-[28rem] bg-[#0F0F0F] border border-white/15 flex flex-col">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <span className="font-clash uppercase tracking-tight text-sm">{t("chat_title")}</span>
            <span className="font-mono-hs text-[10px] text-[#BC5D2E]">● online</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 text-sm ${m.role === "user" ? "bg-[#BC5D2E] text-white" : "bg-[#1A1A1A] text-[#D4D4D4]"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && <div className="font-mono-hs text-xs text-[#737373]">...</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2">
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={t("chat_placeholder")}
              className="hs-input text-sm"
            />
            <button data-testid="chatbot-send" onClick={send} className="text-[#BC5D2E] hover:text-white transition-colors">
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
